import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("missing"); return; }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && tries.current < 15) {
        tries.current += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") { setState("paid"); return; }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      <Seo title="Deposit payment | Appleman Tech Repair" description="Repair deposit payment status." />
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 mx-auto mb-6 animate-spin" />
          <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-3">Confirming your deposit…</h1>
          <p className="text-neutral-600">One moment while Stripe confirms the payment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-accent" />
          <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-4" data-testid="deposit-paid-heading">Deposit received</h1>
          <p className="text-neutral-600 mb-8">Your £10 deposit is confirmed and comes off your final bill. We've emailed a receipt.</p>
          <Link to="/dashboard" data-testid="deposit-dashboard-link"
            className="inline-block border-2 border-black bg-black text-white px-8 py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
            View my repair
          </Link>
        </>
      )}
      {(state === "timeout" || state === "missing") && (
        <>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-4">Still confirming…</h1>
          <p className="text-neutral-600 mb-8">Stripe is taking a little longer than usual. Your deposit status will update in your account shortly.</p>
          <Link to="/dashboard" data-testid="deposit-pending-link"
            className="inline-block border-2 border-black bg-white px-8 py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
            Go to my account
          </Link>
        </>
      )}
    </div>
  );
}
